import { captureHandledException } from '@/lib/sentry';
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import {
  getCompanyUserInvitations,
  updateCompany,
  getAppUsersByCompany,
  updateAppUser,
  updateCompanyUserInvitation,
  addCompanyUserInvitation,
  generateAdminAccount,
  deleteField,
  writeCompanyAudit,
  Timestamp,
} from "@/lib/firebase";
import { useAuth } from "@/contexts/use-auth";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Loader2, ArrowRightCircle, Mail, Users } from "lucide-react";
import { logger } from "@/lib/logger";

interface TrialCompany {
  id: string;
  name: string;
  type?: string;
  trialExpiryDate?: any;
}

interface TrialInvitation {
  id: string;
  email: string;
  status: string;
  created?: any;
}

interface ConvertToLiveModalProps {
  isOpen: boolean;
  onClose: () => void;
  company: TrialCompany;
}

const validateEmail = (email: string): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const formatDate = (ts: any): string => {
  if (!ts) return "—";
  try {
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    return d.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch (error) {
    captureHandledException(error, "trials.parse-date");
    return "—";
  }
};

const statusVariant = (status: string): "default" | "secondary" | "destructive" | "outline" => {
  const s = (status || "").toLowerCase();
  if (s === "pending") return "secondary";
  if (s === "sent" || s === "active") return "default";
  if (s === "error") return "destructive";
  return "outline";
};

export default function ConvertToLiveModal({ isOpen, onClose, company }: ConvertToLiveModalProps) {
  const [existingInvitations, setExistingInvitations] = useState<TrialInvitation[]>([]);
  const [loadingInvitations, setLoadingInvitations] = useState(false);
  const [selectedEmail, setSelectedEmail] = useState<string>("");
  const [useNewEmail, setUseNewEmail] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailError, setEmailError] = useState("");
  const { toast } = useToast();
  const { user } = useAuth();

  useEffect(() => {
    if (isOpen) {
      setSelectedEmail("");
      setUseNewEmail(false);
      setNewEmail("");
      setEmailError("");
      fetchInvitations();
    }
  }, [isOpen, company]);

  const fetchInvitations = async () => {
    setLoadingInvitations(true);
    try {
      const invitations = (await getCompanyUserInvitations(company.id) as any[]).map(d => ({
        id: d.id,
        email: d.email || "",
        status: d.status || "Pending",
        created: d.created,
      }));
      invitations.sort((a, b) => a.email.localeCompare(b.email));
      setExistingInvitations(invitations);

      if (invitations.length === 0) {
        setUseNewEmail(true);
      }
    } catch (error: any) {
      logger.error("❌ Error loading invitations:", error);
    } finally {
      setLoadingInvitations(false);
    }
  };

  const getAdminEmail = (): string => {
    if (useNewEmail) return newEmail.trim();
    return selectedEmail.trim();
  };

  const validate = (): boolean => {
    const email = getAdminEmail();
    if (!email) {
      setEmailError("Please select or enter an administrator email address.");
      return false;
    }
    if (!validateEmail(email)) {
      setEmailError(`"${email}" is not a valid email address.`);
      return false;
    }
    setEmailError("");
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    const adminEmail = getAdminEmail();
    setIsSubmitting(true);
    try {
      // Remove trial expiry from company
      await updateCompany(company.id, { trialExpiryDate: deleteField() });
      logger.log("✅ Removed trialExpiryDate from company:", company.id);

      // Remove trial expiry from all appUsers for this company
      const appUsers = await getAppUsersByCompany(company.id) as any[];
      await Promise.all(appUsers.map(u => updateAppUser(u.id, { trialExpiryDate: deleteField() })));
      logger.log("✅ Removed trialExpiryDate from", appUsers.length, "appUsers");

      let invitationId: string;

      if (!useNewEmail) {
        // Existing user selected — update their invitation record to administrator role
        const existingInvitation = existingInvitations.find(inv => inv.email === selectedEmail);
        if (!existingInvitation) throw new Error("Could not find the selected invitation record.");

        await updateCompanyUserInvitation(existingInvitation.id, { role: "administrator" });
        logger.log("✅ Updated existing invitation to administrator:", existingInvitation.id);

        // Update appUsers record if one exists for this email + company
        const matchingUsers = appUsers.filter(u => u.email === selectedEmail);
        await Promise.all(matchingUsers.map(u => updateAppUser(u.id, { userRole: "administrator" })));
        if (matchingUsers.length > 0) {
          logger.log("✅ Updated appUsers userRole to administrator for", selectedEmail);
        }

        invitationId = existingInvitation.id;
      } else {
        // New email — create a fresh administrator invitation
        invitationId = await addCompanyUserInvitation({
          companyId: company.id,
          email: adminEmail,
          role: "administrator",
          status: "Pending",
          inviteDate: null,
          error: "",
          created: Timestamp.now(),
        });
        logger.log("✅ Administrator invitation created:", invitationId);
      }

      await generateAdminAccount({ id: invitationId });
      logger.log("✅ generate_admin_account called for invitation:", invitationId);

      await writeCompanyAudit(
        company.id,
        company.name,
        'converted_to_live',
        user?.email ?? 'unknown',
        user?.uid ?? '',
        [
          { field: 'trialExpiryDate', from: formatDate(company.trialExpiryDate), to: 'removed' },
          { field: 'adminEmail', from: '', to: adminEmail },
        ]
      );

      toast({
        title: "Trial converted",
        description: `${company.name} is now a live customer. An administrator invitation has been sent to ${adminEmail}.`,
      });

      onClose();
    } catch (error: any) {
      logger.error("❌ Error converting trial:", error);
      toast({
        title: "Failed to convert trial",
        description: error.message || "An unexpected error occurred. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    if (!isSubmitting) onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ArrowRightCircle className="h-5 w-5 text-green-600" />
            Convert to Live — {company.name}
          </DialogTitle>
          <DialogDescription>
            Remove the trial restriction and set up an administrator account for this company. The trial expiry date will be removed from the company and all associated users.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          <div className="space-y-3">
            <Label className="flex items-center gap-1.5">
              <Users className="h-4 w-4" />
              Administrator Account
            </Label>
            <p className="text-xs text-slate-500">
              Choose an existing trial user to become the administrator, or enter a new email address.
            </p>

            {loadingInvitations ? (
              <div className="flex items-center gap-2 text-sm text-slate-500 py-2">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading existing users...
              </div>
            ) : (
              <RadioGroup
                value={useNewEmail ? "__new__" : selectedEmail}
                onValueChange={(val) => {
                  setEmailError("");
                  if (val === "__new__") {
                    setUseNewEmail(true);
                    setSelectedEmail("");
                  } else {
                    setUseNewEmail(false);
                    setSelectedEmail(val);
                  }
                }}
                disabled={isSubmitting}
                className="space-y-2"
              >
                {existingInvitations.map(inv => (
                  <label
                    key={inv.id}
                    htmlFor={`inv-${inv.id}`}
                    className="flex items-center justify-between rounded-md border px-3 py-2.5 gap-3 cursor-pointer hover:bg-slate-50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <RadioGroupItem value={inv.email} id={`inv-${inv.id}`} />
                      <div className="flex items-center gap-2 min-w-0">
                        <Mail className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                        <span className="text-sm truncate">{inv.email}</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      <span className="text-xs text-slate-400">{formatDate(inv.created)}</span>
                      <Badge variant={statusVariant(inv.status)} className="text-xs capitalize">
                        {inv.status}
                      </Badge>
                    </div>
                  </label>
                ))}

                <label
                  htmlFor="inv-new"
                  className="flex items-center gap-3 rounded-md border px-3 py-2.5 cursor-pointer hover:bg-slate-50 has-[[data-state=checked]]:border-primary has-[[data-state=checked]]:bg-primary/5"
                >
                  <RadioGroupItem value="__new__" id="inv-new" />
                  <div className="flex items-center gap-2">
                    <Mail className="h-3.5 w-3.5 text-slate-400 flex-shrink-0" />
                    <span className="text-sm text-slate-600">Enter a new email address</span>
                  </div>
                </label>

                {useNewEmail && (
                  <div className="relative">
                    <Mail className="h-4 w-4 absolute left-3 top-3 text-slate-400" />
                    <Input
                      type="email"
                      value={newEmail}
                      onChange={(e) => {
                        setNewEmail(e.target.value);
                        setEmailError("");
                      }}
                      placeholder="admin@company.com"
                      disabled={isSubmitting}
                      className={`pl-10 ${emailError ? "border-red-500" : ""}`}
                    />
                  </div>
                )}
              </RadioGroup>
            )}

            {emailError && <p className="text-sm text-red-600">{emailError}</p>}
          </div>

          <div className="rounded-md bg-amber-50 border border-amber-200 px-4 py-3">
            <p className="text-sm text-amber-800">
              <strong>This action cannot be undone.</strong> The trial expiry date will be permanently removed and an administrator invitation will be sent.
            </p>
          </div>

          <DialogFooter className="gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || loadingInvitations} className="bg-green-600 hover:bg-green-700">
              {isSubmitting ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Converting...
                </>
              ) : (
                <>
                  <ArrowRightCircle className="h-4 w-4 mr-2" />
                  Convert to Live
                </>
              )}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
